import React, { useState, useEffect, useRef } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

export default function RetailerPlanFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors]                   = useState({});
  const [planList, setPlanList]               = useState([]);
  const [editData, setEditData]               = useState(null);

  // ✅ Searchable Retailer
  const [searchQuery, setSearchQuery]         = useState("");
  const [searchResults, setSearchResults]     = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [showDropdown, setShowDropdown]       = useState(false);
  const searchTimeout                         = useRef(null);

  const { userid } = useSession();

  // ✅ Fetch plan dropdown
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const planForm = new FormData();
        planForm.append("action", "GET");
        planForm.append("userid", userid);
        const planRes  = await fetch("https://api.mokshiq.in/plan_manager_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: planForm,
        });
        const planData = await planRes.json();
        setPlanList(planData.data || []);
      } catch (err) { console.error(err); }
    };
    fetchPlans();
  }, []);

  // ✅ Search retailer with debounce
  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedRetailer(null);
    setShowDropdown(true);
    clearTimeout(searchTimeout.current);
    if (!query.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const form = new FormData();
        form.append("action", "SEARCH_RETAILER");
        form.append("query", query);
        form.append("userid", userid);
        const res    = await fetch("https://api.mokshiq.in/retailer_plan_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: form,
        });
        const result = await res.json();
        setSearchResults(result.data || []);
      } catch (err) { console.error(err); }
    }, 300);
  };

  const handleSelectRetailer = (retailer) => {
    setSelectedRetailer(retailer);
    setSearchQuery(retailer.name);
    setShowDropdown(false);
    setSearchResults([]);
  };

  // ✅ Fetch record by ID when editing
  useEffect(() => {
    if (mode === "edit" && userData?.id) {
      const fetchById = async () => {
        try {
          const form = new FormData();
          form.append("action", "GETBYID");
          form.append("id", userData.id);
          form.append("userid", userid);
          const res    = await fetch("https://api.mokshiq.in/retailer_plan_action", {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            body: form,
          });
          const result = await res.json();
          if (result.status) {
            setEditData(result.data);
            if (result.data.retailer_id) {
              setSelectedRetailer({ id: result.data.retailer_id, name: result.data.retailer_name || "" });
              setSearchQuery(result.data.retailer_name || "");
            }
          }
        } catch (err) { console.error(err); }
      };
      fetchById();
    } else {
      setEditData(null);
      setSelectedRetailer(null);
      setSearchQuery("");
    }
  }, [mode, userData]);

  const validateForm = () => {
    let newErrors = {};
    if (!selectedRetailer)                             newErrors.retailer = "Retailer is required.";
    if (!document.getElementById("plan_id")?.value)   newErrors.plan_id  = "Plan is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) { showMainToast("Please correct the errors before saving.", "danger"); return; }
    const formData = new FormData();
    formData.append("retailer_id", selectedRetailer?.id || "");
    formData.append("plan_id",     document.getElementById("plan_id").value);
    formData.append("is_active",   document.getElementById("is_active").value);
    formData.append("id",          userData?.id || "");
    formData.append("action",      "ADDUpdate");
    formData.append("userid",      userid);
    try {
      const response = await fetch("https://api.mokshiq.in/retailer_plan_action", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: formData,
      });
      const result = await response.json();
      if (result.status) { showMainToast(result.data || "Success!", "success"); onClose(); if (onSuccess) onSuccess(); }
      else showMainToast(result.data || "Something went wrong!", "danger");
    } catch { showMainToast("Something went wrong!", "danger"); }
  };

  const isReady = mode === "add" || (mode === "edit" && editData !== null);

  return (
    <BaseModal
      show={show} onClose={onClose}
      title={mode === "edit" ? "Edit Plan Assignment" : "Assign Plan to Retailer"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        { label: mode === "edit" ? "Update" : "Assign", className: "btn-primary", onClick: handleSave },
      ]}
    >
      <div className="card-body">
        {!isReady ? <div className="text-center py-4">Loading...</div> : (
          <form key={editData?.id || "new"} className="row g-3 app-form rounded-control">

            {/* ✅ Searchable Retailer */}
            <div className="col-md-6" style={{ position: "relative" }}>
              <label className="form-label">Retailer</label>
              <input
                type="text"
                className={`form-control ${errors.retailer ? "is-invalid" : ""}`}
                placeholder="Search retailer by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                autoComplete="off"
              />
              {errors.retailer && <div className="text-danger">{errors.retailer}</div>}
              {showDropdown && searchResults.length > 0 && (
                <ul style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, background: "#fff", border: "1px solid #ddd", borderRadius: "4px", maxHeight: "200px", overflowY: "auto", listStyle: "none", margin: 0, padding: 0, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}>
                  {searchResults.map((r) => (
                    <li key={r.id} onMouseDown={() => handleSelectRetailer(r)}
                      style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", fontSize: "14px" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                    >
                      {r.name} {r.phone ? `— ${r.phone}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Plan */}
            <div className="col-md-6">
              <label className="form-label">Plan</label>
              <select id="plan_id" className={`form-select ${errors.plan_id ? "is-invalid" : ""}`} defaultValue={editData?.plan_id || ""}>
                <option value="">-- Select Plan --</option>
                {planList.map((p) => (
                  <option key={p.id} value={p.id}>{p.Name} — {p["Plan Type"]}</option>
                ))}
              </select>
              {errors.plan_id && <div className="text-danger">{errors.plan_id}</div>}
            </div>

            {/* Status */}
            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select id="is_active" className="form-select" defaultValue={editData?.is_active !== undefined ? String(editData.is_active) : "true"}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

          </form>
        )}
      </div>
    </BaseModal>
  );
}